import React, { useEffect, useMemo, useState } from "react"
import moment from "moment"
import { formatAnyTime } from "utils/commonFunctions"
import { isOnHoliday } from "utils/holidayUtils"
import Swal from "sweetalert2"
import {
  Button,
  Card,
  CardBody,
  Container,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  FormGroup,
  Row,
  Col,
  Table,
} from "reactstrap"
import { Calendar, momentLocalizer } from "react-big-calendar"
import "react-big-calendar/lib/css/react-big-calendar.css"
import Breadcrumb from "components/Common/Breadcrumb"
import { FaCheckDouble, FaEye, FaPen } from "react-icons/fa"
import { MdCancel, MdThumbDown, MdThumbUp } from "react-icons/md"
import { TiTick } from "react-icons/ti"
import {
  fetchData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import Loader from "components/Loader/loader"
import FullCalendar from "@fullcalendar/react"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import { usePermissionData } from "AppContext"
import PermittedComponent from "PermittedComponent"
import { FcCancel } from "react-icons/fc"

const getTotalMinutes = (start, end) => {
  if (!start || !end) return 0

  const parse = time => {
    if (!time) return null

    const is12 = /am|pm/i.test(time)
    let h, m

    if (is12) {
      const [t, ap] = time.split(" ")
      ;[h, m] = t.split(":").map(Number)
      if (ap.toLowerCase() === "pm" && h < 12) h += 12
      if (ap.toLowerCase() === "am" && h === 12) h = 0
    } else {
      ;[h, m] = time.split(":").map(Number)
    }

    return h * 60 + (m || 0)
  }

  let startMin = parse(start)
  let endMin = parse(end)

  if (endMin < startMin) endMin += 1440 // next day

  return endMin - startMin
}

const getCurrentWeekRange = () => {
  const today = new Date()

  const start = new Date(today)
  start.setDate(today.getDate() - today.getDay()) // Sunday

  const end = new Date(start)
  end.setDate(start.getDate() + 6) // Saturday

  return { start, end }
}

const AttendanceReport = () => {
  const { checkAccess } = usePermissionData()

  const [visibleRange, setVisibleRange] = useState({ start: null, end: null })
  const [totalHours, setTotalHours] = useState("")

  const [selectedLocation, setSelectedLocation] = useState("")
  const [selectedEmployee, setSelectedEmployee] = useState("")
  const [locationTypeFilter, setLocationTypeFilter] = useState("")

  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(false)

  const filteredEvents = events.filter(event => {
    /* 📍 Location filter */
    const matchLocation =
      !selectedLocation ||
      String(event?.calender?.locationId ?? event?.calender?.location?.id) ===
        String(selectedLocation)

    /* 👤 Employee filter */
    const matchEmployee =
      !selectedEmployee ||
      String(event?.employee?.id) === String(selectedEmployee)

    /* 🏷️ Type filter (same logic as earlier, FIXED) */
    const locationTypes = (() => {
      try {
        return event?.calender?.location?.type
          ? JSON.parse(event.calender.location.type)
          : []
      } catch {
        return []
      }
    })()

    const matchType =
      !locationTypeFilter?.length ||
      // ✔ match by location type
      locationTypes.some(t => locationTypeFilter.includes(t)) ||
      // ✔ match by employee type
      (locationTypeFilter.includes("window_cleaning") &&
        event?.employee?.isWindowCleaner) ||
      (locationTypeFilter.includes("industrial_cleaning") &&
        event?.employee?.isIndustrialCleaner)

    return matchLocation && matchEmployee && matchType
  })

  // Modal states
  const [editModal, setEditModal] = useState(false)
  const [selectedEvent, setSelectedEvent] = useState(null)

  // Separate state for inputs
  const [startTime, setStartTime] = useState("")
  const [outTime, setoutTime] = useState("")

  const [detailModal, setDetailModal] = useState(false)
  const [selectedEventForDetail, setSelectedEventForDetail] = useState(null)

  const [punchesModal, setPunchesModal] = useState(false)
  const [selectedPunchGroup, setSelectedPunchGroup] = useState(null)

  const [holidays, setHolidays] = useState([])

  const openPunchesModal = event => {
    setSelectedPunchGroup(event)
    setPunchesModal(true)
  }
  const closePunchesModal = () => {
    setPunchesModal(false)
    setSelectedPunchGroup(null)
  }

  useEffect(() => {
    const initialRange = getCurrentWeekRange()
    setVisibleRange(initialRange)
  }, [])

  useEffect(() => {
    getTimeSheet()
    getApprovedHolidays()
  }, [])

  async function getTimeSheet() {
    const APIdata = await fetchData(setLoading, "timesheet")
    if (APIdata?.success) {
      const rawData = APIdata.timesheet.map(item => ({
        ...item,
        start: moment(
          `${item.date} ${item.inTime}`,
          "YYYY-MM-DD HH:mm"
        ).toDate(),
        end: moment(
          `${item.date} ${item.outTime}`,
          "YYYY-MM-DD HH:mm"
        ).toDate(),
      }))

      const grouped = Object.values(
        rawData.reduce((acc, item) => {
          const key = item.calenderSlotId
          if (!acc[key]) {
            acc[key] = { ...item, punches: [item] }
          } else {
            acc[key].punches.push(item)
            if (item.start < acc[key].start) acc[key].start = item.start
            if (item.end > acc[key].end) acc[key].end = item.end
          }
          return acc
        }, {})
      )

      setEvents(grouped)
    }
  }

  async function getApprovedHolidays() {
    const APIdata = await fetchData(setLoading, "holiday")
    if (APIdata?.success) {
      const approved = (APIdata?.data || []).filter(h => h.status === true)
      setHolidays(approved)
    }
  }

  const getEventsInVisibleRange = () => {
    if (!visibleRange.start || !visibleRange.end) return []

    return filteredEvents.filter(ev => {
      if (!ev.start || !ev.end) return false
      return ev.start >= visibleRange.start && ev.start <= visibleRange.end
    })
  }

  const calculateTotalWeekHours = () => {
    const rangeEvents = getEventsInVisibleRange()
    let totalMinutes = 0

    rangeEvents.forEach(ev => {
      const diff = ev.end - ev.start
      totalMinutes += diff / (1000 * 60)
    })

    const hrs = Math.floor(totalMinutes / 60)
    const mins = Math.round(totalMinutes % 60)

    return `${hrs}h ${mins}m`
  }

  useEffect(() => {
    setTotalHours(calculateTotalWeekHours())
  }, [visibleRange, events])

  const convertTo24HourFormat = time12h => {
    if (!time12h) return ""
    const [time, modifier] = time12h.split(" ")
    let [hours, minutes] = time.split(":")

    if (hours === "12") hours = "00"
    if (modifier?.toUpperCase() === "PM")
      hours = String(parseInt(hours, 10) + 12)

    return `${hours.padStart(2, "0")}:${minutes}`
  }

  const calculateTotalHours = (start, end) => {
    if (!start || !end) return ""

    let startDate, endDate

    // Helper to parse time string
    const parseTime = timeStr => {
      if (!timeStr) return null

      let hours, minutes

      // Check if string contains AM/PM
      const is12Hour = /am|pm/i.test(timeStr.trim())

      if (is12Hour) {
        // Example: "2:30 PM"
        const [timePart, ampm] = timeStr.trim().split(" ")
        let [h, m] = timePart.split(":").map(Number)
        if (ampm.toLowerCase() === "pm" && h < 12) h += 12
        if (ampm.toLowerCase() === "am" && h === 12) h = 0
        hours = h
        minutes = m || 0
      } else {
        // 24-hour format "14:30" or "9:05"
        const [h, m] = timeStr.split(":").map(Number)
        hours = h
        minutes = m || 0
      }

      const date = new Date()
      date.setHours(hours, minutes, 0, 0)
      return date
    }

    // Determine startDate
    if (start instanceof Date) startDate = new Date(start)
    else if (typeof start === "string") startDate = parseTime(start)

    // Determine endDate
    if (end instanceof Date) endDate = new Date(end)
    else if (typeof end === "string") endDate = parseTime(end)

    if (!startDate || !endDate) return ""

    // If end is earlier than start → next day
    if (endDate < startDate) endDate.setDate(endDate.getDate() + 1)

    const diffMs = endDate - startDate
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

    return `${diffHrs}h ${diffMins}m`
  }

  // Open modal and set input states from selected event's start/end
  const openEditModal = event => {
    setSelectedEvent(event)
    setStartTime(convertTo24HourFormat(event?.inTime))
    setoutTime(convertTo24HourFormat(event?.outTime))
    setEditModal(true)
  }

  // Close modal and clear states
  const closeEditModal = () => {
    setEditModal(false)
    setSelectedEvent(null)
  }

  const openDetailModal = event => {
    setSelectedEventForDetail(event)
    setDetailModal(true)
  }

  const closeDetailModal = () => {
    setDetailModal(false)
    setSelectedEventForDetail(null)
  }

  const saveTimeChanges = async () => {
    if (isOnHoliday(selectedEvent, holidays)) {
      return Swal.fire(
        "Cannot Update",
        "This timesheet entry falls on an approved holiday for this employee.",
        "warning"
      )
    }

    const result = await Swal.fire({
      title: "Are you Sure",
      text: "you want to update the time for this Job",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
    })

    if (result.isConfirmed) {
      const APIdata = await FetchPutData(
        setLoading,
        "timesheet",
        selectedEvent?.id,
        {
          inTime: startTime,
          outTime,
        }
      )
      if (APIdata?.data?.success) {
        setEvents(prev =>
          prev.map(ev =>
            ev.id === selectedEvent.id
              ? { ...ev, startTime, outTime, onLeave: false }
              : ev
          )
        )
        Swal.fire("Approved!", "Job has been approved.", "success")
      } else {
        Swal.fire(
          "Something Went Wrong!",
          APIdata?.response?.data?.message,
          "error"
        )
      }
      Swal.fire("Updated!", "Time has been updated.", "success")
      closeEditModal()
    }
  }

  // Confirm Approve job
  const confirmApprove = async event => {
    if (isOnHoliday(event, holidays)) {
      return Swal.fire(
        "Cannot Approve",
        "This timesheet entry falls on an approved holiday for this employee.",
        "warning"
      )
    }

    const result = await Swal.fire({
      title: `Approve job for ${event.employee?.name}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
    })

    if (result.isConfirmed) {
      const APIdata = await FetchPutData(setLoading, "timesheet", event?.id, {
        status: true,
      })
      if (APIdata?.data?.success) {
        getTimeSheet()
        setPunchesModal(false)
        Swal.fire("Approved!", "Job has been approved.", "success")
      } else {
        Swal.fire(
          "Something Went Wrong!",
          APIdata?.response?.data?.message,
          "error"
        )
      }
    }
  }

  const confirmPartial = async event => {
    if (isOnHoliday(event, holidays)) {
      return Swal.fire(
        "Cannot Approve",
        "This timesheet entry falls on an approved holiday for this employee.",
        "warning"
      )
    }

    const result = await Swal.fire({
      title: `Approve job Partially for ${event.employee?.name}?`,
      text: "Employee will get Payment For Planned Hours Only",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Approve",
    })

    if (result.isConfirmed) {
      const APIdata = await FetchPutData(
        setLoading,
        "timesheet/alloted/time",
        event?.calenderSlotId,
        {
          status: true,
        }
      )
      if (APIdata?.data?.success) {
        getTimeSheet()
        setPunchesModal(false)
        Swal.fire("Approved!", "Job has been approved Partially.", "success")
      } else {
        Swal.fire(
          "Something Went Wrong!",
          APIdata?.response?.data?.message,
          "error"
        )
      }
    }
  }

  // Confirm Cancel job
  const confirmCancel = async event => {
    const result = await Swal.fire({
      title: `Cancel job for ${event.employee?.name}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Cancel Job",
    })

    if (result.isConfirmed) {
      const APIdata = await FetchPutData(setLoading, "timesheet", event?.id, {
        status: false,
      })
      if (APIdata?.data?.success) {
        getTimeSheet()
        setPunchesModal(false)
        Swal.fire("Disapproved!", "Job has been disapproved.", "success")
      } else {
        Swal.fire(
          "Something Went Wrong!",
          APIdata?.response?.data?.message,
          "error"
        )
      }
    }
  }

  const handleGlobalApprove = async () => {
    const holidayEntries = getEventsInVisibleRange().filter(e =>
      isOnHoliday(e, holidays)
    )
    if (holidayEntries.length > 0) {
      const msg = `${holidayEntries.length} timesheet entr${
        holidayEntries.length === 1 ? "y" : "ies"
      } for approved holiday${
        holidayEntries.length === 1 ? "" : "s"
      } will be skipped.`
      await Swal.fire("Holiday Entries Found", msg, "info")
    }

    const result = await Swal.fire({
      title: "Approve all Timesheets?",
      text:
        holidayEntries.length > 0
          ? `${holidayEntries.length} holiday entr${
              holidayEntries.length === 1 ? "y" : "ies"
            } will be skipped.`
          : "This will approve all Timesheets in the calendar.",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, approve all",
    })

    if (result.isConfirmed) {
      const APIdata = await FetchPostData(
        setLoading,
        "timesheet/bulk/approval",
        {
          fromDate: moment(visibleRange?.start).format("YYYY-MM-DD"),
          toDate: moment(visibleRange?.end).format("YYYY-MM-DD"),
          status: true,
        }
      )
      if (APIdata?.data?.success) {
        getTimeSheet()
        Swal.fire("Approved!", APIdata?.data?.message, "success")
      } else {
        Swal.fire(
          "Something went wrong",
          APIdata?.response?.data?.message,
          "error"
        )
      }
    }
  }

  const handleGlobalCancel = async () => {
    const result = await Swal.fire({
      title: "Cancel all Timesheets?",
      text: "This will reject all timesheets in the calendar.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, cancel all",
    })

    if (result.isConfirmed) {
      const APIdata = await FetchPostData(
        setLoading,
        "timesheet/bulk/approval",
        {
          fromDate: moment(visibleRange?.start).format("YYYY-MM-DD"),
          toDate: moment(visibleRange?.end).format("YYYY-MM-DD"),
          status: false,
        }
      )
      if (APIdata?.data?.success) {
        getTimeSheet()
        Swal.fire("Rejected!", APIdata?.data?.message, "success")
      } else {
        Swal.fire(
          "Something went wrong",
          APIdata?.response?.data?.message,
          "error"
        )
      }
    }
  }

  const uniqueLocations = [
    ...new Map(
      events.map(e => [e?.calender?.location?.id, e?.calender?.location])
    ).values(),
  ]

  const uniqueEmployees = [
    ...new Map(events?.map(e => [e?.employee?.id, e?.employee])).values(),
  ]

  // Custom event rendering
  const EventComponent = info => {
    const fcEvent = info.event
    const event = fcEvent.extendedProps
    if (!event && !fcEvent.id) return null

    const punches = event.punches || []
    const anyTimeAdded = punches.some(p => p.inTime && p.outTime)
    const holiday = isOnHoliday(event, holidays)

    const parseTimeToMinutes = timeStr => {
      if (!timeStr) return null
      const str = timeStr.trim()
      const is12Hour = /am|pm/i.test(str)
      if (is12Hour) {
        const [timePart, ampm] = str.split(" ")
        let [h, m] = timePart.split(":").map(Number)
        if (ampm.toLowerCase() === "pm" && h < 12) h += 12
        if (ampm.toLowerCase() === "am" && h === 12) h = 0
        return h * 60 + (m || 0)
      }
      const [h, m] = str.split(":").map(Number)
      return (h || 0) * 60 + (m || 0)
    }

    const totalHours = (() => {
      let total = 0
      punches.forEach(p => {
        const inMin = parseTimeToMinutes(p.inTime)
        const outMin = parseTimeToMinutes(p.outTime)
        if (inMin != null && outMin != null && outMin > inMin) {
          total += outMin - inMin
        }
      })
      const h = Math.floor(total / 60)
      const m = total % 60
      return `${h} hr${h !== 1 ? "s" : ""} ${m} min`
    })()

    // p.status == 1 catches true/1/"1"; p.status == 0 catches false/0/"0" but NOT null
    const approvedCount = punches.filter(p => p.status == 1).length
    const rejectedCount = punches.filter(p => p.status == 0).length

    let backgroundColor
    if (approvedCount === 0 && rejectedCount === 0) {
      backgroundColor = "#f1b44c" // all pending → yellow
    } else if (approvedCount === rejectedCount) {
      backgroundColor = "#3d6eb6" // tie → blue
    } else if (approvedCount > rejectedCount) {
      backgroundColor = "#4caf50" // majority approved → green
    } else {
      backgroundColor = "#888888" // majority rejected → grey
    }

    return (
      <div
        className="text-white p-2 w-100"
        style={{ cursor: "pointer", backgroundColor, minWidth: 0 }}
        onClick={() => openPunchesModal(event)}
      >
        <p className="mb-1 text-start" style={{ fontSize: "10.5px" }}>
          Planned: {formatAnyTime(event?.calender?.inTime)} -{" "}
          {formatAnyTime(event?.calender?.outTime)}
        </p>
        <h6 className="my-1 fw-bold text-start text-wrap-safe" style={{ fontSize: "12px" }}>
          {event?.calender?.location?.name}
        </h6>
        <div className="text-start text-wrap-safe" style={{ fontSize: "12px" }}>
          {event?.employee?.name} (£{event?.employee?.hourlyRate}/hr)
        </div>
        <div className="mt-1 text-start">
          <span style={{ fontSize: "10.5px" }}>Total Hours: {totalHours}</span>
        </div>
        <div className="mt-2 d-flex align-items-center gap-2 flex-wrap">
          {holiday && (
            <span className="badge bg-danger">
              <MdCancel className="me-1" />
              Holiday
            </span>
          )}
          <span className="badge bg-white text-dark">
            {punches.length} punch{punches.length !== 1 ? "es" : ""}
          </span>
          {approvedCount > 0 && (
            <span className="badge bg-success">
              {approvedCount}/{punches.length} Approved
            </span>
          )}
          {!anyTimeAdded && (
            <span className="badge bg-danger">Time Not Added</span>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="page-content px-1">
      <PermittedComponent isAccess={checkAccess(2, 1) || checkAccess(2, 2)}>
        {loading && <Loader />}

        <Container fluid className="px-0 px-md-2">
          <div className="px-2 px-md-4">
            <Breadcrumb
              title={"Dashboards"}
              breadcrumbItem={"Timesheet Report"}
              description={"View and Manage Timesheet of Employees"}
            />
          </div>
          <Card>
            <CardBody>
              {/* Title + Filters row */}
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <h5 className="mb-0 fw-bold">Employee Timesheet Calendar</h5>
                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <select
                    className="form-select rounded-4 w-auto"
                    value={locationTypeFilter}
                    onChange={e => setLocationTypeFilter(e.target.value)}
                  >
                    <option value="">All (Both CleaningType)</option>
                    <option value="window_cleaning">Window Cleaning</option>
                    <option value="industrial_cleaning">
                      Industrial Cleaning
                    </option>
                  </select>
                  <select
                    className="form-select rounded-4 filter-select"
                    value={selectedLocation}
                    onChange={e => setSelectedLocation(e.target.value)}
                  >
                    <option value="">Select All Locations</option>
                    {uniqueLocations
                      .filter(i => i?.id)
                      .map(loc => (
                        <option key={loc?.id} value={loc?.id}>
                          {loc?.name}
                        </option>
                      ))}
                  </select>
                  <select
                    className="form-select rounded-4 filter-select"
                    value={selectedEmployee}
                    onChange={e => setSelectedEmployee(e.target.value)}
                  >
                    <option value="">Select All Employees</option>
                    {uniqueEmployees
                      .filter(i => i.id)
                      .map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <Card>
                <CardBody>
                  <FullCalendar
                    plugins={[dayGridPlugin, timeGridPlugin]}
                    initialView="dayGridWeek"
                    events={filteredEvents}
                    eventContent={EventComponent}
                    customButtons={{
                      totalHours: {
                        text: `⏱ Total: ${totalHours}`,
                        click: () => {},
                      },
                      approveAll: {
                        text: "✔ Approve All",
                        click: handleGlobalApprove,
                      },
                      rejectAll: {
                        text: "✕ Reject All",
                        click: handleGlobalCancel,
                      },
                    }}
                    dayHeaderContent={args => {
                      const date = args.date
                      const d = String(date.getDate()).padStart(2, "0")
                      const m = String(date.getMonth() + 1).padStart(2, "0")
                      const y = date.getFullYear()
                      const weekday = date.toLocaleDateString("en-GB", {
                        weekday: "short",
                      })
                      return (
                        <div className="text-center lh-sm py-1">
                          <div
                            className="fw-semibold text-uppercase"
                            style={{ fontSize: "11px", letterSpacing: "0.5px" }}
                          >
                            {weekday}
                          </div>
                          <div className="fw-bold" style={{ fontSize: "13px" }}>
                            {d}-{m}-{y}
                          </div>
                        </div>
                      )
                    }}
                    headerToolbar={{
                      left: "prev,next today",
                      center: "totalHours approveAll rejectAll",
                      right: "title",
                    }}
                    views={{
                      timeGridWeek: { buttonText: "Week" },
                      timeGridDay: { buttonText: "Day" },
                      dayGridMonth: { buttonText: "Month" },
                    }}
                  />
                </CardBody>
              </Card>
            </CardBody>
          </Card>
        </Container>

        {/* Edit Time Modal */}
        <Modal isOpen={editModal} toggle={closeEditModal} centered>
          <ModalHeader toggle={closeEditModal}>
            Edit Time for {selectedEvent?.employee?.name}
          </ModalHeader>
          <ModalBody
            className="pb-0"
            style={{ paddingBottom: "0px !importent" }}
          >
            <Row>
              <Col md={4}>
                <FormGroup>
                  <Label for="startTime">
                    Start Time <span className="text-danger">*</span>
                  </Label>
                  <input
                    id="startTime"
                    type="time"
                    className="form-control"
                    value={startTime}
                    onChange={e => setStartTime(e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="outTime">
                    End Time <span className="text-danger">*</span>
                  </Label>
                  <input
                    id="outTime"
                    type="time"
                    className="form-control"
                    value={outTime}
                    onChange={e => setoutTime(e.target.value)}
                  />
                </FormGroup>
              </Col>
              <Col md={4}>
                <FormGroup>
                  <Label for="outTime">Total Hrs</Label>
                  <input
                    id="outTime"
                    type="text"
                    disabled
                    className="form-control"
                    value={calculateTotalHours(startTime, outTime)}
                  />
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="primary" onClick={saveTimeChanges}>
              Save
            </Button>{" "}
            <Button color="secondary" onClick={closeEditModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>

        <Modal
          isOpen={detailModal}
          toggle={closeDetailModal}
          centered
          size="lg"
        >
          <ModalHeader toggle={closeDetailModal}>
            Timesheet Details - {selectedEventForDetail?.employee?.name}
          </ModalHeader>
          <ModalBody className="bg-white">
            {selectedEventForDetail && (
              <Row>
                <Col md={5}>
                  <div className="mb-4">
                    <strong>Date:</strong>{" "}
                    {moment(selectedEventForDetail.start).format(
                      "dddd, DD/MM/YYYY"
                    )}
                  </div>
                  <div className="mb-4">
                    <strong>Employee:</strong>{" "}
                    {selectedEventForDetail?.employee?.name}
                  </div>
                  <div className="mb-4">
                    <strong>Hourly Rate:</strong> £
                    {selectedEventForDetail?.employee?.hourlyRate} / hr
                  </div>
                  <div className="mb-4">
                    <strong>Status:</strong>{" "}
                    <span
                      className={`badge p-2 bg-${
                        selectedEventForDetail.status === "1"
                          ? "success"
                          : selectedEventForDetail.status === "0"
                          ? "secondary"
                          : "warning"
                      }`}
                    >
                      {selectedEventForDetail.status === "1"
                        ? "Approved"
                        : selectedEventForDetail.status === "0"
                        ? "Rejected"
                        : "Pending"}
                    </span>
                  </div>
                </Col>
                <Col md={7}>
                  <div className="mb-4">
                    <strong>Location:</strong>{" "}
                    {selectedEventForDetail?.calender?.location?.name}
                  </div>
                  <div className="mb-4 d-flex justify-content-between">
                    <div>
                      <strong>InTime:</strong>{" "}
                      {selectedEventForDetail.inTime
                        ? moment(
                            `${selectedEventForDetail?.calender?.date} ${selectedEventForDetail.inTime}`
                          ).format("h:mm A")
                        : "Not Added"}
                    </div>
                    <div>
                      <strong>OutTime:</strong>{" "}
                      {selectedEventForDetail.outTime
                        ? moment(
                            `${selectedEventForDetail?.calender.date} ${selectedEventForDetail.outTime}`
                          ).format("h:mm A")
                        : "Not Added"}
                    </div>
                    <div>
                      <strong>Total Hrs:</strong>{" "}
                      {selectedEventForDetail.inTime &&
                      selectedEventForDetail.outTime
                        ? calculateTotalHours(
                            selectedEventForDetail.inTime,
                            selectedEventForDetail.outTime
                          )
                        : "Not Calculated"}
                    </div>
                  </div>
                  <div className="mb-4 d-flex gap-2">
                    <p className="fw-bold mb-0">Punched In Address :</p>{" "}
                    <p className="d-inlineblock mb-0">
                      {selectedEventForDetail.punchInAddress}
                    </p>
                  </div>
                  <div className="mb-2 d-flex gap-2">
                    <p className="fw-bold mb-0">Punched Out Address :</p>{" "}
                    <p className="d-inlineblock mb-0">
                      {selectedEventForDetail.punchOutAddress}
                    </p>
                  </div>
                </Col>
              </Row>
            )}
          </ModalBody>
          <ModalFooter className="bg-white">
            {selectedEventForDetail?.start && selectedEventForDetail?.end && (
              <>
                <Button
                  color="primary"
                  onClick={() => {
                    closeDetailModal()
                    openEditModal(selectedEventForDetail)
                  }}
                >
                  <FaPen className="me-2" /> Edit Time
                </Button>
                <Button
                  color="success"
                  onClick={() => {
                    closeDetailModal()
                    confirmApprove(selectedEventForDetail)
                  }}
                >
                  Approve
                </Button>
                <Button
                  color="danger"
                  onClick={() => {
                    closeDetailModal()
                    confirmCancel(selectedEventForDetail)
                  }}
                >
                  Reject
                </Button>
              </>
            )}
            <Button color="secondary" onClick={closeDetailModal}>
              Close
            </Button>
          </ModalFooter>
        </Modal>

        <Modal isOpen={punchesModal} toggle={closePunchesModal} size="xl">
          <ModalHeader
            toggle={closePunchesModal}
            className="fw-bold text-primary d-flex align-items-center gap-3"
          >
            <span>
              Punch Details — {selectedPunchGroup?.calender?.location?.name} |{" "}
              {moment(selectedPunchGroup?.calender?.date).format("DD/MM/YYYY")}
              {isOnHoliday(selectedPunchGroup, holidays) && (
                <span className="badge bg-danger ms-2 fs-7">
                  <MdCancel className="me-1" />
                  On Holiday
                </span>
              )}
            </span>
          </ModalHeader>
          <ModalBody>
            {/* Employee & Job Details */}
            <div className="border rounded-3 p-3 mb-4 bg-light">
              <Row className="align-items-center">
                {/* Avatar + Name */}
                <Col
                  md={4}
                  className="d-flex align-items-center gap-3 mb-2 mb-md-0"
                >
                  <img
                    src={
                      selectedPunchGroup?.employee?.profilePicture
                        ? `${process.env.REACT_APP_IMAGE_PATH}${selectedPunchGroup.employee.profilePicture}`
                        : "https://ui-avatars.com/api/?name=" +
                          encodeURIComponent(
                            selectedPunchGroup?.employee?.name || "E"
                          ) +
                          "&background=3d6eb6&color=fff&size=64"
                    }
                    alt="employee"
                    className="rounded-circle"
                    width={52}
                    height={52}
                    style={{ objectFit: "cover" }}
                  />
                  <div>
                    <div className="fw-bold fs-6">
                      {selectedPunchGroup?.employee?.name}
                    </div>
                    <div
                      className="text-muted text-capitalize"
                      style={{ fontSize: "12px" }}
                    >
                      {selectedPunchGroup?.employee?.position || "—"}
                    </div>
                  </div>
                </Col>

                {/* Employee Info */}
                <Col md={4} className="mb-2 mb-md-0">
                  <div className="mb-1" style={{ fontSize: "13px" }}>
                    <span className="text-muted">Email: </span>
                    <span className="fw-semibold">
                      {selectedPunchGroup?.employee?.email || "—"}
                    </span>
                  </div>
                  <div className="mb-1" style={{ fontSize: "13px" }}>
                    <span className="text-muted">Phone: </span>
                    <span className="fw-semibold">
                      {selectedPunchGroup?.employee?.phone || "—"}
                    </span>
                  </div>
                  <div style={{ fontSize: "13px" }}>
                    <span className="text-muted">Rate: </span>
                    <span className="fw-semibold">
                      £{selectedPunchGroup?.employee?.hourlyRate}/hr
                    </span>
                  </div>
                </Col>

                {/* Job Info */}
                <Col md={4} className="mb-2 mb-md-0">
                  <div className="mb-1" style={{ fontSize: "13px" }}>
                    <span className="text-muted">Location: </span>
                    <span className="fw-semibold">
                      {selectedPunchGroup?.calender?.location?.name || "—"}
                    </span>
                  </div>
                  <div className="mb-1" style={{ fontSize: "13px" }}>
                    <span className="text-muted">Date: </span>
                    <span className="fw-semibold">
                      {selectedPunchGroup?.calender?.date
                        ? moment(selectedPunchGroup?.calender?.date).format(
                            "DD/MM/YYYY"
                          )
                        : "—"}
                    </span>
                  </div>
                  <div style={{ fontSize: "13px" }}>
                    <span className="text-muted">Planned: </span>
                    <span className="fw-semibold">
                      {formatAnyTime(selectedPunchGroup?.calender?.inTime)} –{" "}
                      {formatAnyTime(selectedPunchGroup?.calender?.outTime)}
                    </span>
                  </div>
                </Col>
              </Row>
            </div>

            {selectedPunchGroup?.punches?.some(p => {
              const planned = getTotalMinutes(
                selectedPunchGroup?.calender?.inTime,
                selectedPunchGroup?.calender?.outTime
              )
              return (
                getTotalMinutes(p.inTime, p.outTime) < planned &&
                p.status === null
              )
            }) && (
              <div className="d-flex justify-content-end">
                <Button
                  color="warning"
                  size="sm"
                  className="d-flex align-items-center gap-1 fw-semibold mb-3"
                  onClick={() =>
                    confirmPartial(selectedPunchGroup?.punches?.[0])
                  }
                >
                  <FaCheckDouble size={12} /> Approve Planned Hours
                </Button>
              </div>
            )}

            <Table bordered responsive hover className="align-middle shadow-sm">
              <thead className="bg-dark">
                <tr>
                  <th className="text-white">#</th>
                  <th className="text-white">In Time</th>
                  <th className="text-white">Out Time</th>
                  <th className="text-white">Actual Out</th>
                  <th className="text-white">Total Hrs</th>
                  <th className="text-white">Grace Period</th>
                  <th className="text-white">Punch In Address</th>
                  <th className="text-white">Punch Out Address</th>
                  <th className="text-white">Status</th>
                  <th className="text-white">Actions</th>
                </tr>
              </thead>
              <tbody>
                {selectedPunchGroup?.punches?.map((punch, idx) => {
                  const plannedMins = getTotalMinutes(
                    selectedPunchGroup?.calender?.inTime,
                    selectedPunchGroup?.calender?.outTime
                  )
                  const actualMins = getTotalMinutes(
                    punch.inTime,
                    punch.outTime
                  )

                  return (
                    <tr key={punch.id}>
                      <td>{idx + 1}</td>
                      <td>{formatAnyTime(punch.inTime) || "—"}</td>
                      <td>{formatAnyTime(punch.outTime) || "—"}</td>
                      <td>{formatAnyTime(punch.actualOutTime) || "—"}</td>
                      <td>
                        {punch.inTime && punch.outTime
                          ? calculateTotalHours(punch.inTime, punch.outTime)
                          : "—"}
                      </td>
                      <td>
                        {punch.graceTime ? (
                          <span
                            className={`badge px-2 py-1 ${
                              punch.isGraceAddition
                                ? "bg-success"
                                : punch.isGraceDeduction
                                ? "bg-warning text-dark"
                                : "bg-secondary"
                            }`}
                          >
                            {punch.isGraceAddition
                              ? `+${punch.graceTime}`
                              : punch.isGraceDeduction
                              ? `-${punch.graceTime}`
                              : punch.graceTime}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={{ fontSize: "12px", maxWidth: "160px" }}>
                        {punch.punchInAddress || "—"}
                      </td>
                      <td style={{ fontSize: "12px", maxWidth: "160px" }}>
                        {punch.punchOutAddress || "—"}
                      </td>
                      <td>
                        <span
                          className={`badge px-2 py-1 ${
                            punch.status === "1"
                              ? "bg-success"
                              : punch.status === "0"
                              ? "bg-secondary"
                              : "bg-warning text-dark"
                          }`}
                        >
                          {punch.status === "1"
                            ? "Approved"
                            : punch.status === "0"
                            ? "Rejected"
                            : "Pending"}
                        </span>
                      </td>
                      <td>
                        <div className="d-flex gap-1">
                          <Button
                            size="sm"
                            className="bg-dark px-2"
                            title="Edit"
                            onClick={() => openDetailModal(punch)}
                          >
                            <FaPen size={12} />
                          </Button>
                          {punch.status !== "1" && (
                            <Button
                              size="sm"
                              color="success"
                              className="px-2"
                              title="Approve"
                              onClick={() => confirmApprove(punch)}
                            >
                              <TiTick size={14} />
                            </Button>
                          )}

                          {punch.status !== "0" && (
                            <Button
                              size="sm"
                              color="light"
                              className="px-2"
                              title="Reject"
                              onClick={() => confirmCancel(punch)}
                            >
                              <FcCancel size={14} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
          </ModalBody>
        </Modal>
      </PermittedComponent>
    </div>
  )
}

export default AttendanceReport
